import type { ChatSnapshot as ConversationSnapshot, OpenCloudUIMessage } from "./types.js";

export type ChildAgentPart = Extract<OpenCloudUIMessage["parts"][number], { type: "data-child" }>;

export function childAgentName(purpose: string | null): string {
  const value = purpose?.trim();
  if (!value) return "Sub-agent";
  // Older runs used a thread preview, which can contain the harness prompt.
  if (/^#*\s*OpenCloud\b[^\n]*\bharness\b/i.test(value)) return "Sub-agent";
  const bounded = value.replace(/\s+/g, " ");
  if (bounded.length > 120) return `${bounded.slice(0, 119).trimEnd()}…`;
  // Human-written task descriptions keep their wording and capitalization.
  if (!/^[a-z\d]+(?:[_-][a-z\d]+)+$/i.test(value)) return bounded;
  const name = value.replace(/^opencloud[_-]/i, "").replace(/[_-]+/g, " ");
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function childStateIsLive(state: ChildAgentPart["data"]["state"]): boolean {
  return state === "queued" || state === "active" || state === "waiting";
}

export function childStateLabel(state: ChildAgentPart["data"]["state"]): string {
  if (state === "queued") return "Queued";
  if (state === "active") return "Working";
  if (state === "waiting") return "Waiting";
  if (state === "succeeded") return "Finished";
  if (state === "failed") return "Needs attention";
  return "Stopped";
}

export function childStateDetail(state: ChildAgentPart["data"]["state"]): string {
  if (state === "queued") return "Waiting to start.";
  if (state === "active") return "Working on its assigned task.";
  if (state === "waiting") return "Waiting for the main agent before continuing.";
  if (state === "succeeded") return "Returned its result to the main agent.";
  if (state === "failed") return "Could not finish its assigned task.";
  return "Stopped before finishing its assigned task.";
}

export function childFinishedAgo(updatedAt: string, now: number): string | null {
  const timestamp = Date.parse(updatedAt);
  if (!Number.isFinite(timestamp)) return null;
  const minutes = Math.floor(Math.max(0, now - timestamp) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1_440) return `${Math.floor(minutes / 60)}h ago`;
  return `${Math.floor(minutes / 1_440)}d ago`;
}

/** Reconcile retained parts by invocation, including legacy run-only snapshots. */
export function conversationChildAgents(snapshot: ConversationSnapshot): ChildAgentPart[] {
  const children = new Map<string, ChildAgentPart>();
  for (const run of snapshot.runs) {
    if (!run.parentRunId) continue;
    const state = run.childOutcome ?? (run.status === "completed" ? "succeeded"
      : run.status === "failed" ? "failed" : run.status === "cancelled" ? "cancelled"
      : run.status === "queued" ? "queued"
      : ["paused", "waiting_for_user"].includes(run.status) ? "waiting" : "active");
    children.set(run.id, {
      type: "data-child", id: `child:${run.parentRunId}:${run.id}`,
      data: {
        runId: run.id, parentRunId: run.parentRunId, purpose: run.childPurpose,
        state, outcome: run.childOutcome, occurredAt: run.createdAt,
        updatedAt: run.updatedAt,
      },
    });
  }
  for (const message of snapshot.uiMessages ?? []) {
    for (const part of message.parts) {
      if (part.type !== "data-child") continue;
      const previous = children.get(part.data.runId);
      // A late name update preserves a finished child's timestamp. Prefer it
      // on equal timestamps/states without reviving a settled snapshot.
      if (!previous || Date.parse(part.data.updatedAt) > Date.parse(previous.data.updatedAt)
        || (part.data.state === previous.data.state
          && Date.parse(part.data.updatedAt) === Date.parse(previous.data.updatedAt))) {
        children.set(part.data.runId, part);
      }
    }
  }
  // A later lifecycle update is not a new finish time.
  for (const run of snapshot.runs) {
    const part = children.get(run.id);
    if (part && run.finishedAt && !childStateIsLive(part.data.state)) {
      children.set(run.id, { ...part, data: { ...part.data, updatedAt: run.finishedAt } });
    }
  }
  return [...children.values()].sort((left, right) =>
    Number(childStateIsLive(right.data.state)) - Number(childStateIsLive(left.data.state))
    || Date.parse(right.data.updatedAt) - Date.parse(left.data.updatedAt)
    || left.data.runId.localeCompare(right.data.runId));
}
